import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { queryKeys } from "../../lib/queryKeys";
import { salesApi } from "../../lib/sales-api";
import type { RepActivity, RepLoad, SalesReport as Report } from "../../lib/sales-types";
import { cn } from "../../components/ui/cn";
import type { User } from "../../types";

/**
 * The manager's view of the desk.
 *
 * A different question from the Today board, which asks "what should I ring
 * next". This one is opened to ask whether somebody is falling behind, so
 * nearly all of it is per person -- a desk total cannot answer that, and the
 * comparison between one rep and the next is the entire point.
 *
 * ## Two clocks, kept visibly apart
 *
 * The first table is **now**: who is holding what, and how much of it is late.
 * It ignores the period control entirely, because "how many calls is Rachel
 * holding" is a fact about this moment and asking it of last March is
 * meaningless.
 *
 * Everything below is **within the period**, because those are things that
 * happened. They are separate sections with their own headings rather than
 * extra columns on one table, since a single row mixing "holding 9" with "rang
 * 40 last month" invites reading one as the other.
 */

/**
 * Daily, weekly, monthly.
 *
 * The "last" variants are deliberately not here as periods of their own any
 * more: yesterday, last week and last month are the *baselines* these three
 * are measured against, shown as a delta on every figure. Offering them as
 * separate choices as well would mean two ways to ask the same question, and
 * the one that reads "Last week" on its own answers a worse version of it --
 * a finished week with nothing to compare against.
 *
 * The values are the strings the shared `resolveRange` understands, so a month
 * here is the same month as on the helpdesk dashboard.
 */
const PERIODS = [
  { value: "today", label: "Today", against: "yesterday" },
  { value: "this-week", label: "This week", against: "last week to date" },
  { value: "this-month", label: "This month", against: "last month to date" },
] as const;

export function SalesReport({ user }: { user: User }) {
  const [range, setRange] = useState<string>("this-week");

  const report = useQuery({
    queryKey: queryKeys.salesReport(range),
    queryFn: () => salesApi.report(range),
    staleTime: 60_000,
  });

  const data = report.data;

  return (
    <div className="mx-auto flex h-full w-full max-w-5xl flex-col overflow-y-auto p-4 sm:p-6">
      <header className="mb-4 flex flex-wrap items-center gap-3">
        <h1 className="text-lg font-semibold text-content">Dashboard</h1>

        <select
          value={range}
          onChange={(event) => setRange(event.target.value)}
          aria-label="Period"
          className="ml-auto h-8 rounded-md border border-line bg-surface px-2 text-xs text-content outline-none focus:border-brand-400"
        >
          {PERIODS.map((period) => (
            <option key={period.value} value={period.value}>
              {period.label}
            </option>
          ))}
        </select>
      </header>

      {report.isLoading ? (
        <p className="p-8 text-center text-xs text-faint">Loading…</p>
      ) : !data ? (
        <p className="p-8 text-center text-xs text-faint">
          The dashboard could not be loaded.
        </p>
      ) : (
        <>
          <LoadTable rows={data.load} currentUserId={user.id} />
          <ActivityTable
            rows={data.activity}
            currentUserId={user.id}
            against={
              PERIODS.find((entry) => entry.value === range)?.against ?? ""
            }
          />
          <ScoreSplit scores={data.scores} />
        </>
      )}
    </div>
  );
}

/**
 * Who is holding what, right now.
 *
 * Sorted most-overdue first by the server, because "is anybody drowning" is
 * the question this is opened for and the answer belongs at the top rather
 * than alphabetically under whoever is called Adam.
 */
function LoadTable({
  rows,
  currentUserId,
}: {
  rows: RepLoad[];
  currentUserId: number;
}) {
  return (
    <Panel
      title="Open calls, by person"
      note="As things stand now — not affected by the period"
    >
      {rows.length === 0 ? (
        <Empty>Nobody is holding an open call.</Empty>
      ) : (
        <Table head={["Person", "Assigned", "Overdue"]}>
          {rows.map((row) => (
            <Row key={row.userId} mine={row.userId === currentUserId}>
              <Name name={row.name} mine={row.userId === currentUserId} />
              <Cell>{row.assigned}</Cell>
              <Cell>
                {/* Red only when there is something to be red about. A column
                    of coloured noughts trains people to ignore the colour. */}
                {row.overdue > 0 ? (
                  <span className="font-medium text-sla-breached">
                    {row.overdue}
                  </span>
                ) : (
                  <span className="text-faint">0</span>
                )}
              </Cell>
            </Row>
          ))}
        </Table>
      )}
    </Panel>
  );
}

function ActivityTable({
  rows,
  currentUserId,
  against,
}: {
  rows: RepActivity[];
  currentUserId: number;
  against: string;
}) {
  return (
    <Panel title="Calls made, by person" note={`Against ${against}`}>
      {rows.length === 0 ? (
        <Empty>Nobody rang anybody in this period.</Empty>
      ) : (
        <Table
          head={["Person", "Attempts", "Answered", "Closed", "Won", "Success"]}
        >
          {rows.map((row) => (
            <Row key={row.userId} mine={row.userId === currentUserId}>
              <Name name={row.name} mine={row.userId === currentUserId} />
              <Cell>
                <Delta now={row.attempts} before={row.previous.attempts} />
              </Cell>
              <Cell>
                <Delta now={row.answered} before={row.previous.answered} />
              </Cell>
              <Cell>
                <Delta now={row.closed} before={row.previous.closed} />
              </Cell>
              <Cell>
                <Delta now={row.won} before={row.previous.won} />
              </Cell>
              <Cell>
                {/* A dash, not 0%. Nothing decided is not the same as nothing
                    won, and the second reads as an accusation. */}
                {row.successRate === null ? (
                  <span className="text-faint">—</span>
                ) : (
                  `${Math.round(row.successRate * 100)}%`
                )}
              </Cell>
            </Row>
          ))}
        </Table>
      )}
    </Panel>
  );
}

/**
 * A figure with how it compares to the same slice of the period before.
 *
 * The arrow is small and sits after the number, because the number is the
 * answer and the arrow is context -- a delta rendered as loudly as the figure
 * makes a table of eight rows unreadable.
 *
 * Nothing is shown when the two are equal, and nothing when both are zero.
 * "0 (no change)" is three characters of noise on a row that already said
 * nought, and a column of grey zeroes is what stops anybody reading the
 * column at all.
 *
 * Colour is not the only signal: the arrow points, and the number carries a
 * sign. Roughly one person in twelve cannot reliably separate the green from
 * the red, and this is a table people are judged on.
 */
function Delta({ now, before }: { now: number; before: number }) {
  const change = now - before;

  return (
    <span className="inline-flex items-baseline gap-1">
      <span>{now}</span>
      {change === 0 ? null : (
        <span
          title={`${before} in the same period before`}
          className={cn(
            "text-[10px] font-medium",
            change > 0
              ? "text-emerald-600 dark:text-emerald-400"
              : "text-red-600 dark:text-red-400",
          )}
        >
          {change > 0 ? "▲" : "▼"}
          {Math.abs(change)}
        </span>
      )}
    </span>
  );
}

/**
 * How closed calls actually went, desk-wide.
 *
 * Under the per-person rates rather than above them, and showing counts rather
 * than only a headline percentage: a success rate on its own hides whether it
 * came from four calls or four hundred, which is the first thing anybody
 * should know before acting on it.
 */
function ScoreSplit({ scores }: { scores: Report["scores"] }) {
  const total = scores.reduce((sum, score) => sum + score.total, 0);

  return (
    <Panel title="How calls were closed" note="Every score used in the period">
      {total === 0 ? (
        <Empty>No calls were closed in this period.</Empty>
      ) : (
        <ul className="divide-y divide-line">
          {scores.map((score) => (
            <li
              key={score.scoreId}
              className="flex items-center gap-3 px-3 py-1.5 text-xs"
            >
              <span
                className={cn(
                  "shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium",
                  toneClass(score.tone),
                )}
              >
                {score.label}
              </span>

              {/* A bar as well as a number: the split between four scores is a
                  shape, and reading four percentages to see it is work. */}
              <span className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-surface-2">
                <span
                  className={cn("block h-full rounded-full", barClass(score.tone))}
                  style={{ width: `${(score.total / total) * 100}%` }}
                />
              </span>

              <span className="shrink-0 tabular-nums text-muted">
                {score.total}
              </span>
              <span className="w-10 shrink-0 text-right tabular-nums text-faint">
                {Math.round((score.total / total) * 100)}%
              </span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/* Tone, never the label -- the score list is a manager's to rename, and a desk
   that calls success "Won" must not lose its colour. Mirrors `toneClass` in
   CallRow, kept separate because that one styles a pill and this one a bar. */
function toneClass(tone: string): string {
  if (tone === "positive") {
    return "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300";
  }
  if (tone === "negative") {
    return "bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300";
  }
  return "bg-surface-2 text-muted-on-tint";
}

function barClass(tone: string): string {
  if (tone === "positive") return "bg-emerald-500";
  if (tone === "negative") return "bg-red-500";
  return "bg-slate-400";
}

// ── Shared furniture ─────────────────────────────────────────────────────────

function Panel({
  title,
  note,
  children,
}: {
  title: string;
  note?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-5 rounded-lg border border-line bg-surface">
      <header className="flex flex-wrap items-baseline gap-2 border-b border-line px-3 py-2">
        <h2 className="text-sm font-medium text-content">{title}</h2>
        {note ? <span className="text-[11px] text-faint">{note}</span> : null}
      </header>
      {children}
    </section>
  );
}

function Table({
  head,
  children,
}: {
  head: string[];
  children: React.ReactNode;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead className="text-left text-[11px] uppercase tracking-wide text-faint">
          <tr>
            {head.map((label, index) => (
              <th
                key={label}
                className={cn(
                  "px-3 py-1.5 font-medium",
                  index > 0 && "text-right",
                )}
              >
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function Row({
  mine,
  children,
}: {
  mine: boolean;
  children: React.ReactNode;
}) {
  return (
    <tr
      className={cn(
        "border-t border-line",
        /* Your own row marked, so the comparison has a fixed point. Finding
           yourself in a list of names is what anybody does first. */
        mine && "bg-brand-50/60 dark:bg-brand-950/30",
      )}
    >
      {children}
    </tr>
  );
}

function Name({ name, mine }: { name: string; mine: boolean }) {
  return (
    <td className="px-3 py-1.5 text-xs text-content">
      {name}
      {mine ? <span className="ml-1.5 text-[10px] text-faint">you</span> : null}
    </td>
  );
}

function Cell({ children }: { children: React.ReactNode }) {
  return (
    <td className="px-3 py-1.5 text-right text-xs tabular-nums text-muted">
      {children}
    </td>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="p-4 text-center text-xs text-faint">{children}</p>;
}
